import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  H1,
  Paragraph,
  Button,
  ChoiceChips,
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  Image,
  Separator,
} from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { trackEvent } from '@fs/zion-analytics'
import getPlaceName from '../../../../shared/services/standards/places'
import ThankYouOverlay from './images/ThankYouOverlay.png'

export default function ReviewCompletionDialog({
  dialogOverlay,
  initialChipValue,
  chipOptions,
  totalFinished,
  handleContinue,
  goBackToGive,
  placeCode,
  name,
  goToPreviousStep,
}) {
  const [t] = useTranslation()
  const [chipValue, setChipValue] = useState(initialChipValue)
  const [placeName, setPlaceName] = useState('')

  useEffect(() => {
    if (dialogOverlay.isOpen) {
      setChipValue(initialChipValue)
    }
  }, [dialogOverlay.isOpen, chipOptions, initialChipValue])

  const chipsProps = useMemo(
    () => ({
      name: 'continueNameCount',
      chips: chipOptions.map((option) => ({
        label: option.label,
        value: option.value,
        selected: option.value === chipValue,
      })),
      onSelectionChange: (chip) => {
        if (chip) setChipValue(chip.value)
      },
    }),
    [chipOptions, chipValue]
  )

  useEffect(() => {
    let fetching = true
    // Funcitons
    function unmount() {
      fetching = false
    }
    async function getPlaceData() {
      const { displayName } = await getPlaceName(placeCode, i18n.language)

      if (fetching) setPlaceName(displayName.name)
    }

    // Logic
    getPlaceData()
    return unmount
  }, [placeCode])

  const displayName = getDisplayName(name)
  const nameElement = displayName ? <strong key={displayName}>{displayName}</strong> : null

  let completionText
  const completionTextKey = null
  if (totalFinished > 1) {
    completionText = t('completion-dialog.description', { totalFinished, placeName })
  } else if (totalFinished === 1 && displayName?.length) {
    completionText = t('completion-dialog.description2', { name: displayName ?? '', placeName })
  } else if (totalFinished === 1 && !displayName?.length) {
    completionText = t('completion-dialog.description.noname')
  }

  function getDisplayName(originalName) {
    return originalName.includes('undefined') || !originalName ? null : originalName
  }

  const handleClose = useCallback(() => {
    // check if goToPreviousStep exist otherwise just use goBackToGive
    if (goToPreviousStep) {
      goToPreviousStep()
    } else {
      trackEvent({
        event_name: 'click_action',
        link_name: `To GIVE: Finished Dialog`,
      })
      goBackToGive()
    }
  }, [goBackToGive, goToPreviousStep])

  return (
    <DialogOverlay {...dialogOverlay} closeOnClickAway={false} close={handleClose} nonClosable={!goToPreviousStep}>
      <Image src={ThankYouOverlay} alt={t('completion-dialog.thank-you', 'Thank you!')} />
      <Separator size="xxs" />
      <H1 size="H5">{t('completion-dialog.bold-text', "You're helping others find their family!")}</H1>
      <Separator size="xxs" />
      <Paragraph size="sm">
        {t(completionTextKey, completionText, {
          totalFinished: <strong key={totalFinished}>{totalFinished}</strong>,
          placeName: <strong key={placeName}>{placeName}</strong>,
          name: nameElement,
        })}
      </Paragraph>
      <Paragraph size="sm">
        {t('task-viewer.finished-modal.continue-reviewing-prompt', 'Want to make more names available?')}
      </Paragraph>
      <Separator size="xxs" />
      <ChoiceChips {...chipsProps} />
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            data-testid="maybe-later-button"
            linkName="Maybe Later Action"
            onClick={() => {
              trackEvent({
                event_name: 'click_action',
                link_name: `To GIVE: Finished Dialog`,
              })
              goBackToGive()
            }}
          >
            {t('task-viewer.finished-modal.cancel-btn', 'Maybe Later')}
          </Button>
          <Button
            data-testid="continue-button"
            linkName="Continue Action"
            onClick={() => {
              const selectedChip = chipOptions.find((chip) => chip.value === chipValue)
              const max = selectedChip ? selectedChip.max : undefined
              trackEvent({
                event_name: 'click_action',
                link_name: `Continue with ${max === null ? 'unlimited' : max} names`,
              })
              handleContinue(max)
            }}
            emphasis="high"
          >
            {t('task-viewer.finished-modal.continue-btn', 'Continue')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
